import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { User, UserRole, Agent, Client } from '../types';
import { UserIcon, PencilIcon, TrashIcon, KeyIcon, BuildingStorefrontIcon } from '@heroicons/react/24/outline';

interface UserManagementProps {
  onClose?: () => void;
}

const UserManagement: React.FC<UserManagementProps> = ({ onClose }) => {
  const { user, token, hasRole } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editingRole, setEditingRole] = useState<UserRole>('user');
  const [showPasswordModal, setShowPasswordModal] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showAssignmentModal, setShowAssignmentModal] = useState<User | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedAgentIds, setSelectedAgentIds] = useState<number[]>([]);
  const [selectedClientIds, setSelectedClientIds] = useState<number[]>([]);
  const [showSelectAllAgents, setShowSelectAllAgents] = useState(false);
  const [showSelectAllClients, setShowSelectAllClients] = useState(false);

  useEffect(() => {
    if (hasRole(['admin', 'moderator'])) {
      fetchUsers();
      fetchAgentsAndClients();
    }
  }, [hasRole, token]);

  // Обработка клавиши Esc для закрытия модального окна и всех вложенных модалок
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Закрываем вложенные модалки в порядке приоритета
        if (showPasswordModal) {
          setShowPasswordModal(null);
          setNewPassword('');
          setConfirmPassword('');
        } else if (showAssignmentModal) {
          setShowAssignmentModal(null);
          setSelectedAgentIds([]);
          setSelectedClientIds([]);
        } else if (editingUser) {
          setEditingUser(null);
          setEditingRole('user');
        } else if (onClose) {
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [onClose, showPasswordModal, showAssignmentModal, editingUser]);

  // Только админ и модератор могут управлять пользователями
  if (!hasRole(['admin', 'moderator'])) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-700">У вас нет прав для управления пользователями.</p>
      </div>
    );
  }

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/auth/users', {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Не удалось загрузить список пользователей');
      }

      const usersData = await response.json();
      setUsers(usersData);
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Произошла ошибка при загрузке пользователей');
      }
    } finally {
      setLoading(false);
    }
  };

  const getRoleDisplayName = (role: string) => {
    const roleNames = {
      admin: 'Администратор',
      moderator: 'Модератор',
      user: 'Пользователь',
      guest: 'Гость'
    };
    return roleNames[role as keyof typeof roleNames] || role;
  };

  const getRoleColor = (role: string) => {
    const roleColors = {
      admin: 'bg-purple-100 text-purple-800',
      moderator: 'bg-blue-100 text-blue-800',
      user: 'bg-green-100 text-green-800',
      guest: 'bg-gray-100 text-gray-800'
    };
    return roleColors[role as keyof typeof roleColors] || 'bg-gray-100 text-gray-800';
  };

  const handleEditRole = (editUser: User) => {
    setEditingUser(editUser);
    setEditingRole(editUser.role);
  };

  const fetchAgentsAndClients = async () => {
    try {
      const [agentsRes, clientsRes] = await Promise.all([
        fetch('/api/agents', {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch('/api/clients', {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
      ]);

      if (agentsRes.ok) {
        const agentsData = await agentsRes.json();
        setAgents(agentsData);
      }

      if (clientsRes.ok) {
        const clientsData = await clientsRes.json();
        setClients(clientsData);
      }
    } catch (error) {
      console.error('Error fetching agents and clients:', error);
    }
  };

  const handleEditAssignment = (editUser: User) => {
    setShowAssignmentModal(editUser);
    // Don't set assignments for admin/moderator as they have access to all
    if (!['admin', 'moderator'].includes(editUser.role)) {
      // Set the current assignments (supporting both old and new format)
      setSelectedAgentIds(editUser.agentIds || (editUser.agentId ? [editUser.agentId] : []));
      setSelectedClientIds(editUser.clientIds || (editUser.clientId ? [editUser.clientId] : []));
    } else {
      setSelectedAgentIds([]);
      setSelectedClientIds([]);
    }
  };

  const handleSaveAssignment = async () => {
    if (!showAssignmentModal) return;

    try {
      const response = await fetch(`/api/auth/users/${showAssignmentModal.id}/assignment`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          agentIds: selectedAgentIds,
          clientIds: selectedClientIds
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Не удалось обновить привязку пользователя');
      }

      // Обновляем список пользователей
      await fetchUsers();
      setShowAssignmentModal(null);
      setSelectedAgentIds([]);
      setSelectedClientIds([]);
      setError(null);
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Произошла ошибка при обновлении привязки');
      }
    }
  };

  const handleChangePassword = (editUser: User) => {
    setShowPasswordModal(editUser);
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleSavePassword = async () => {
    if (!showPasswordModal) return;

    if (newPassword !== confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }

    if (newPassword.length < 6) {
      setError('Пароль должен содержать не менее 6 символов');
      return;
    }

    try {
      const response = await fetch(`/api/auth/users/${showPasswordModal.id}/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ password: newPassword }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Не удалось изменить пароль');
      }

      setShowPasswordModal(null);
      setNewPassword('');
      setConfirmPassword('');
      setError(null);
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Произошла ошибка при изменении пароля');
      }
    }
  };

  const handleDeleteUser = async (deleteUser: User) => {
    if (!hasRole(['admin'])) {
      setError('Только администратор может удалять пользователей');
      return;
    }

    if (deleteUser.id === user?.id) {
      setError('Нельзя удалить самого себя');
      return;
    }

    if (!window.confirm(`Вы уверены, что хотите удалить пользователя "${deleteUser.username}"? Это действие нельзя отменить.`)) {
      return;
    }

    try {
      const response = await fetch(`/api/auth/users/${deleteUser.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Не удалось удалить пользователя');
      }

      // Обновляем список пользователей
      await fetchUsers();
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Произошла ошибка при удалении пользователя');
      }
    }
  };

  const handleSaveRole = async () => {
    if (!editingUser) return;

    try {
      const response = await fetch(`/api/auth/users/${editingUser.id}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ role: editingRole }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Не удалось изменить роль пользователя');
      }

      // Обновляем список пользователей
      await fetchUsers();
      setEditingUser(null);
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Произошла ошибка при изменении роли');
      }
    }
  };

  const availableRoles: { value: UserRole; label: string }[] = [
    { value: 'user', label: 'Пользователь' },
  ];

  if (hasRole(['admin'])) {
    availableRoles.push(
      { value: 'moderator', label: 'Модератор' },
      { value: 'admin', label: 'Администратор' }
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
        <span className="ml-2 text-gray-600">Загрузка пользователей...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Градиентный заголовок */}
      <div className="relative mb-4 sm:mb-8 bg-gradient-to-r from-purple-600 via-pink-600 to-indigo-600 rounded-xl sm:rounded-2xl p-4 sm:p-8 text-white overflow-hidden shadow-xl">
        {/* Декоративные элементы */}
        <div className="absolute top-0 right-0 -mt-4 -mr-16 w-32 h-32 bg-white opacity-10 rounded-full"></div>
        <div className="absolute bottom-0 left-0 -mb-8 -ml-8 w-20 h-20 bg-white opacity-10 rounded-full"></div>
        <div className="absolute top-1/2 right-1/4 w-16 h-16 bg-white opacity-5 rounded-full"></div>

        <div className="relative z-10">
          <div className="flex items-start justify-between mb-4 sm:mb-6">
            <div className="flex items-center flex-1 min-w-0">
              <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-full p-2 sm:p-4 mr-2 sm:mr-4 flex-shrink-0">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2">
                  Управление пользователями
                </h1>
                <p className="text-white text-opacity-90 text-sm sm:text-base lg:text-lg">
                  👥 Администрирование ролей, паролей и доступа к данным
                </p>
              </div>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 p-2 rounded-full hover:bg-white hover:bg-opacity-20 transition-all duration-200 flex-shrink-0 ml-2"
                title="Закрыть"
              >
                <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Краткая статистика */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
            <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-5 border border-white border-opacity-20">
              <div className="flex items-center justify-between mb-2">
                <div className="text-white text-opacity-80 text-sm font-medium">👥 Всего</div>
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              </div>
              <div className="text-2xl font-bold">{users.length}</div>
            </div>

            <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-5 border border-white border-opacity-20">
              <div className="text-white text-opacity-80 text-sm font-medium mb-2">👑 Админов</div>
              <div className="text-lg font-semibold">
                {users.filter(u => u.role === 'admin').length}
              </div>
            </div>

            <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-5 border border-white border-opacity-20">
              <div className="text-white text-opacity-80 text-sm font-medium mb-2">🛡️ Модераторов</div>
              <div className="text-lg font-semibold">
                {users.filter(u => u.role === 'moderator').length}
              </div>
            </div>

            <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-5 border border-white border-opacity-20">
              <div className="text-white text-opacity-80 text-sm font-medium mb-2">👤 Пользователей</div>
              <div className="text-lg font-semibold">
                {users.filter(u => u.role === 'user').length}
              </div>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 shadow-sm">
          <div className="flex">
            <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="ml-auto pl-3"
            >
              <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <div className="bg-white shadow-xl rounded-2xl p-8 border border-gray-100 hover:shadow-2xl transition-shadow duration-300">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 gap-4">
          <div className="flex items-center">
            <div className="bg-gradient-to-r from-purple-500 to-pink-600 rounded-full p-2 sm:p-3 mr-3 sm:mr-4">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">📋 Список пользователей</h3>
          </div>
          <button
            onClick={fetchUsers}
            className="group relative overflow-hidden rounded-lg sm:rounded-xl py-2 sm:py-3 px-4 sm:px-6 text-sm font-semibold transition-all duration-300 transform bg-gradient-to-r from-emerald-50 to-green-50 border-2 border-emerald-200 text-emerald-700 hover:from-emerald-100 hover:to-green-100 hover:border-emerald-300 hover:text-emerald-800 hover:scale-[1.01] hover:shadow-lg w-full sm:w-auto"
          >
            <div className="absolute inset-0 bg-emerald-500 opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
            <div className="relative flex items-center justify-center">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              🔄 Обновить
            </div>
          </button>
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-2">
          <table className="min-w-full divide-y divide-gray-200 bg-white rounded-xl shadow-sm">
            <thead className="bg-gradient-to-r from-purple-50 to-pink-50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider rounded-tl-xl">
                  👤 Пользователь
                </th>
                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                  💼 Роль
                </th>
                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                  🏢 Агент
                </th>
                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                  🏭 Клиент
                </th>
                {hasRole(['admin', 'moderator']) && (
                  <th className="px-6 py-4 text-right text-sm font-bold text-gray-700 uppercase tracking-wider rounded-tr-xl">
                    ⚙️ Действия
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {users.map((listUser, index) => (
                <tr key={listUser.id} className="hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 transition-all duration-200">
                  <td className="px-6 py-5 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full flex items-center justify-center mr-4">
                        <span className="text-white text-lg font-bold">
                          {listUser.username.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <div className="text-lg font-semibold text-gray-900">
                          {listUser.username}
                        </div>
                        <div className="text-sm text-gray-500">
                          🏷️ ID: {listUser.id}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-5 whitespace-nowrap">
                    {editingUser?.id === listUser.id ? (
                      <div className="flex items-center space-x-2">
                        <select
                          value={editingRole}
                          onChange={(e) => setEditingRole(e.target.value as UserRole)}
                          className="text-sm border border-gray-300 rounded-xl px-3 py-2 bg-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        >
                          {availableRoles.map(role => (
                            <option key={role.value} value={role.value}>
                              {role.label}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={handleSaveRole}
                          className="inline-flex items-center px-3 py-2 rounded-xl text-xs font-bold bg-green-100 text-green-700 hover:bg-green-200 transition-colors duration-200"
                        >
                          ✅ Сохранить
                        </button>
                        <button
                          onClick={() => setEditingUser(null)}
                          className="inline-flex items-center px-3 py-2 rounded-xl text-xs font-bold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors duration-200"
                        >
                          ❌ Отмена
                        </button>
                      </div>
                    ) : (
                      <span className={`inline-flex px-4 py-2 text-sm font-bold rounded-full shadow-sm ${getRoleColor(listUser.role)}`}>
                        {listUser.role === 'admin' && '👑 '}
                        {listUser.role === 'moderator' && '🛡️ '}
                        {listUser.role === 'user' && '👤 '}
                        {getRoleDisplayName(listUser.role)}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-900">
                    {listUser.agentIds && listUser.agentIds.length > 0 ? (
                      <span className="inline-flex items-center px-3 py-2 rounded-lg bg-blue-100 text-blue-800 font-semibold">
                        🏢 {listUser.agentIds.length} агент(ов)
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-3 py-2 rounded-lg bg-gray-100 text-gray-600">
                        {listUser.agentId || '— Нет'}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-900">
                    {listUser.clientIds && listUser.clientIds.length > 0 ? (
                      <span className="inline-flex items-center px-3 py-2 rounded-lg bg-green-100 text-green-800 font-semibold">
                        🏭 {listUser.clientIds.length} клиент(ов)
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-3 py-2 rounded-lg bg-gray-100 text-gray-600">
                        {listUser.clientId || '— Нет'}
                      </span>
                    )}
                  </td>
                  {hasRole(['admin', 'moderator']) && (
                    <td className="px-6 py-5 whitespace-nowrap text-right text-sm font-medium">
                      {listUser.id !== user?.id && editingUser?.id !== listUser.id && (
                        <div className="flex justify-end space-x-2">
                          {hasRole(['admin']) && (
                            <button
                              onClick={() => handleEditRole(listUser)}
                              className="inline-flex items-center p-2 rounded-xl bg-indigo-50 text-indigo-600 hover:bg-indigo-100 hover:text-indigo-700 transition-all duration-200 shadow-sm"
                              title="Изменить роль"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleEditAssignment(listUser)}
                            className="inline-flex items-center p-2 rounded-xl bg-green-50 text-green-600 hover:bg-green-100 hover:text-green-700 transition-all duration-200 shadow-sm"
                            title="Изменить привязку агента/клиента"
                          >
                            <BuildingStorefrontIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleChangePassword(listUser)}
                            className="inline-flex items-center p-2 rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 transition-all duration-200 shadow-sm"
                            title="Сменить пароль"
                          >
                            <KeyIcon className="h-4 w-4" />
                          </button>
                          {hasRole(['admin']) && (
                            <button
                              onClick={() => handleDeleteUser(listUser)}
                              className="inline-flex items-center p-2 rounded-xl bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 transition-all duration-200 shadow-sm"
                              title="Удалить пользователя"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      )}
                      {listUser.id === user?.id && (
                        <span className="inline-flex items-center px-3 py-2 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 text-purple-800 font-semibold text-xs">
                          🙋‍♂️ Это вы
                        </span>
                      )}
                    </td>
                  )}
              </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-4">
          {users.map((listUser, index) => (
            <div key={listUser.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-lg transition-all duration-200">
              {/* User Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full flex items-center justify-center mr-3">
                    <span className="text-white text-sm font-bold">
                      {listUser.username.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{listUser.username}</div>
                    <div className="text-xs text-gray-500">ID: {listUser.id}</div>
                  </div>
                </div>
                {listUser.id === user?.id && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 text-purple-800 font-semibold text-xs">
                    🙋‍♂️ Вы
                  </span>
                )}
              </div>

              {/* User Info */}
              <div className="space-y-3">
                {/* Role */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">💼 Роль:</span>
                  {editingUser?.id === listUser.id ? (
                    <div className="flex items-center space-x-2">
                      <select
                        value={editingRole}
                        onChange={(e) => setEditingRole(e.target.value as UserRole)}
                        className="text-xs border border-gray-300 rounded-lg px-2 py-1 bg-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        {availableRoles.map(role => (
                          <option key={role.value} value={role.value}>
                            {role.label}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={handleSaveRole}
                        className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-bold bg-green-100 text-green-700 hover:bg-green-200 transition-colors duration-200"
                      >
                        ✅
                      </button>
                      <button
                        onClick={() => setEditingUser(null)}
                        className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-bold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors duration-200"
                      >
                        ❌
                      </button>
                    </div>
                  ) : (
                    <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full shadow-sm ${getRoleColor(listUser.role)}`}>
                      {listUser.role === 'admin' && '👑 '}
                      {listUser.role === 'moderator' && '🛡️ '}
                      {listUser.role === 'user' && '👤 '}
                      {getRoleDisplayName(listUser.role)}
                    </span>
                  )}
                </div>

                {/* Agent */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">🏢 Агент:</span>
                  {listUser.agentIds && listUser.agentIds.length > 0 ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-lg bg-blue-100 text-blue-800 font-semibold text-xs">
                      {listUser.agentIds.length} агент(ов)
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-lg bg-gray-100 text-gray-600 text-xs">
                      {listUser.agentId || 'Нет'}
                    </span>
                  )}
                </div>

                {/* Client */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">🏭 Клиент:</span>
                  {listUser.clientIds && listUser.clientIds.length > 0 ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-lg bg-green-100 text-green-800 font-semibold text-xs">
                      {listUser.clientIds.length} клиент(ов)
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-lg bg-gray-100 text-gray-600 text-xs">
                      {listUser.clientId || 'Нет'}
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              {hasRole(['admin', 'moderator']) && listUser.id !== user?.id && editingUser?.id !== listUser.id && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex flex-wrap gap-2">
                    {hasRole(['admin']) && (
                      <button
                        onClick={() => handleEditRole(listUser)}
                        className="flex-1 min-w-0 inline-flex items-center justify-center px-3 py-2 rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100 hover:text-indigo-700 transition-all duration-200 shadow-sm text-sm font-medium"
                      >
                        <PencilIcon className="h-4 w-4 mr-1" />
                        Роль
                      </button>
                    )}
                    <button
                      onClick={() => handleEditAssignment(listUser)}
                      className="flex-1 min-w-0 inline-flex items-center justify-center px-3 py-2 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 hover:text-green-700 transition-all duration-200 shadow-sm text-sm font-medium"
                    >
                      <BuildingStorefrontIcon className="h-4 w-4 mr-1" />
                      Доступ
                    </button>
                    <button
                      onClick={() => handleChangePassword(listUser)}
                      className="flex-1 min-w-0 inline-flex items-center justify-center px-3 py-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 transition-all duration-200 shadow-sm text-sm font-medium"
                    >
                      <KeyIcon className="h-4 w-4 mr-1" />
                      Пароль
                    </button>
                    {hasRole(['admin']) && (
                      <button
                        onClick={() => handleDeleteUser(listUser)}
                        className="flex-1 min-w-0 inline-flex items-center justify-center px-3 py-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 transition-all duration-200 shadow-sm text-sm font-medium"
                      >
                        <TrashIcon className="h-4 w-4 mr-1" />
                        Удалить
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {users.length === 0 && (
          <div className="text-center py-12">
            <div className="flex flex-col items-center">
              <div className="bg-gradient-to-r from-gray-100 to-gray-200 rounded-full p-6 mb-4">
                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
              </div>
              <p className="text-lg font-medium text-gray-500 mb-2">👥 Нет пользователей</p>
              <p className="text-sm text-gray-400">Создайте пользователей через "Создать пользователя"</p>
            </div>
          </div>
        )}
      </div>

      {/* Модальное окно смены пароля */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="relative mx-auto p-4 sm:p-8 border w-full max-w-md shadow-2xl rounded-2xl sm:rounded-3xl bg-white">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full p-3 mr-4">
                  <KeyIcon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">
                  🔑 Смена пароля
                </h3>
              </div>
              <button
                onClick={() => {
                  setShowPasswordModal(null);
                  setNewPassword('');
                  setConfirmPassword('');
                  setError(null);
                }}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4">
              <p className="text-sm text-gray-700">
                👤 Пользователь: <span className="font-semibold">{showPasswordModal.username}</span>
              </p>
            </div>

            <div className="space-y-6">
              <div>
                <label htmlFor="newPassword" className="block text-sm font-semibold text-gray-700 mb-2">
                  🔍 Новый пароль
                </label>
                <input
                  type="password"
                  id="newPassword"
                  className="block w-full px-4 py-3 border-2 border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Введите новый пароль"
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 mb-2">
                  🔄 Подтвердите пароль
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  className="block w-full px-4 py-3 border-2 border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Повторите новый пароль"
                />
              </div>

              {error && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="text-sm text-red-700">{error}</div>
                </div>
              )}

              <div className="flex space-x-4 pt-4">
                <button
                  onClick={handleSavePassword}
                  disabled={!newPassword || !confirmPassword}
                  className="flex-1 group relative overflow-hidden rounded-2xl py-4 px-6 text-lg font-semibold text-white transition-all duration-300 transform bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 hover:scale-[1.02] hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                  <div className="relative flex items-center justify-center">
                    💾 Сохранить пароль
                  </div>
                </button>

                <button
                  onClick={() => {
                    setShowPasswordModal(null);
                    setNewPassword('');
                    setConfirmPassword('');
                    setError(null);
                  }}
                  className="flex-1 group relative overflow-hidden rounded-2xl py-4 px-6 text-lg font-semibold transition-all duration-300 transform bg-gradient-to-r from-gray-50 to-gray-100 border-2 border-gray-200 text-gray-700 hover:from-gray-100 hover:to-gray-200 hover:border-gray-300 hover:text-gray-900 hover:scale-[1.02] hover:shadow-lg"
                >
                  <div className="absolute inset-0 bg-gray-500 opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
                  <div className="relative flex items-center justify-center">
                    ❌ Отмена
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно редактирования привязки */}
      {showAssignmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="relative mx-auto p-4 sm:p-8 border w-full max-w-2xl shadow-2xl rounded-2xl sm:rounded-3xl bg-white">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="bg-gradient-to-r from-green-500 to-teal-600 rounded-full p-3 mr-4">
                  <BuildingStorefrontIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    🏭 Привязка доступа
                  </h3>
                  <p className="text-sm text-gray-600">Пользователь: {showAssignmentModal.username}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowAssignmentModal(null);
                  setSelectedAgentIds([]);
                  setSelectedClientIds([]);
                  setError(null);
                }}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {['admin', 'moderator'].includes(showAssignmentModal.role) && (
              <div className="mb-6 bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-200 rounded-xl p-4">
                <div className="flex items-center">
                  <div className="bg-amber-100 rounded-full p-2 mr-3">
                    <svg className="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-amber-800">
                      {showAssignmentModal.role === 'admin' ? '👑 Администратор' : '🛡️ Модератор'} имеет доступ ко всем данным.
                    </p>
                    <p className="text-xs text-amber-700">Настройка привязок не требуется</p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Агенты {['admin', 'moderator'].includes(showAssignmentModal.role) ? '(не редактируется)' : ''}
                  </label>
                  {!['admin', 'moderator'].includes(showAssignmentModal.role) && (
                    <div className="space-x-2">
                      <button
                        onClick={() => setSelectedAgentIds(agents.map(a => a.id))}
                        className="text-xs text-indigo-600 hover:text-indigo-900"
                      >
                        Выбрать все
                      </button>
                      <span className="text-xs text-gray-400">|</span>
                      <button
                        onClick={() => setSelectedAgentIds([])}
                        className="text-xs text-red-600 hover:text-red-900"
                      >
                        Снять все
                      </button>
                    </div>
                  )}
                </div>
                <div className={`max-h-48 overflow-y-auto border-2 border-gray-200 rounded-xl p-4 space-y-3 bg-white ${['admin', 'moderator'].includes(showAssignmentModal.role) ? 'opacity-50 bg-gray-50' : ''}`}>
                  {agents.length === 0 ? (
                    <p className="text-sm text-gray-500">Загрузка агентов...</p>
                  ) : (
                    agents.map(agent => (
                      <label key={agent.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={selectedAgentIds.includes(agent.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedAgentIds([...selectedAgentIds, agent.id]);
                            } else {
                              setSelectedAgentIds(selectedAgentIds.filter(id => id !== agent.id));
                            }
                          }}
                          disabled={['admin', 'moderator'].includes(showAssignmentModal.role)}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded disabled:opacity-50"
                        />
                        <span className={`text-sm ${['admin', 'moderator'].includes(showAssignmentModal.role) ? 'text-gray-400' : 'text-gray-700'}`}>
                          {agent.name}
                        </span>
                      </label>
                    ))
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {['admin', 'moderator'].includes(showAssignmentModal.role)
                    ? 'Имеет доступ ко всем агентам автоматически'
                    : `Выбрано: ${selectedAgentIds.length} из ${agents.length}`}
                </p>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-2xl p-6">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center">
                    <div className="bg-green-100 rounded-full p-2 mr-3">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.20f7.20m3 5.51c3.04-.01 5.5-2.46 5.51-5.5V10c0-4.42-3.58-8-8-8s-8 3.58-8 8v1.51c0 3.04 2.46 5.49 5.5 5.5z" />
                      </svg>
                    </div>
                    <label className="text-lg font-bold text-gray-800">
                      🏭 Клиенты {['admin', 'moderator'].includes(showAssignmentModal.role) ? '(не редактируется)' : ''}
                    </label>
                  </div>
                  {!['admin', 'moderator'].includes(showAssignmentModal.role) && (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setSelectedClientIds(clients.map(c => c.id))}
                        className="inline-flex items-center px-3 py-2 rounded-xl text-xs font-bold bg-green-100 text-green-700 hover:bg-green-200 transition-colors duration-200"
                      >
                        ✅ Выбрать все
                      </button>
                      <button
                        onClick={() => setSelectedClientIds([])}
                        className="inline-flex items-center px-3 py-2 rounded-xl text-xs font-bold bg-red-100 text-red-700 hover:bg-red-200 transition-colors duration-200"
                      >
                        ❌ Снять все
                      </button>
                    </div>
                  )}
                </div>
                <div className={`max-h-48 overflow-y-auto border-2 border-gray-200 rounded-xl p-4 space-y-3 bg-white ${['admin', 'moderator'].includes(showAssignmentModal.role) ? 'opacity-50 bg-gray-50' : ''}`}>
                  {clients.length === 0 ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="text-center">
                        <div className="bg-gray-100 rounded-full p-3 mx-auto mb-2 w-12 h-12 flex items-center justify-center">
                          <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l7-3 7 3z" />
                          </svg>
                        </div>
                        <p className="text-sm text-gray-500">Загрузка клиентов...</p>
                      </div>
                    </div>
                  ) : (
                    clients.map(client => (
                      <label key={client.id} className={`flex items-center space-x-3 p-3 rounded-xl hover:bg-green-50 transition-colors duration-200 cursor-pointer ${selectedClientIds.includes(client.id) ? 'bg-green-50 border-2 border-green-200' : 'border-2 border-transparent'}`}>
                        <input
                          type="checkbox"
                          checked={selectedClientIds.includes(client.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedClientIds([...selectedClientIds, client.id]);
                            } else {
                              setSelectedClientIds(selectedClientIds.filter(id => id !== client.id));
                            }
                          }}
                          disabled={['admin', 'moderator'].includes(showAssignmentModal.role)}
                          className="h-5 w-5 text-green-600 focus:ring-green-500 border-gray-300 rounded disabled:opacity-50"
                        />
                        <div className="flex items-center">
                          <div className="bg-green-100 rounded-lg p-2 mr-3">
                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l7-3 7 3z" />
                            </svg>
                          </div>
                          <span className={`text-sm font-medium ${['admin', 'moderator'].includes(showAssignmentModal.role) ? 'text-gray-400' : 'text-gray-800'}`}>
                            {client.name}
                          </span>
                        </div>
                      </label>
                    ))
                  )}
                </div>
                <div className="mt-4 bg-green-100 rounded-xl p-3">
                  <p className="text-sm font-medium text-green-800">
                    {['admin', 'moderator'].includes(showAssignmentModal.role)
                      ? 'ℹ️ Автоматический доступ ко всем клиентам'
                      : `📊 Выбрано: ${selectedClientIds.length} из ${clients.length} клиентов`}
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 rounded-2xl border-2 border-gray-200">
                <div className="flex items-center mb-4">
                  <div className="bg-gray-200 rounded-full p-2 mr-3">
                    <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h4 className="text-sm font-bold text-gray-700">📚 Как работают привязки:</h4>
                </div>
                <div className="space-y-3 text-sm text-gray-700">
                  <div className="flex items-start space-x-3">
                    <span className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></span>
                    <p><strong>Без привязки:</strong> пользователь видит все фотографии</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></span>
                    <p><strong>С выбранными агентами:</strong> видит фотографии только этих агентов</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="w-2 h-2 bg-yellow-400 rounded-full mt-2 flex-shrink-0"></span>
                    <p><strong>С выбранными клиентами:</strong> видит фотографии только этих клиентов</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <span className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0"></span>
                    <p><strong>Агенты + клиенты:</strong> видит фотографии выбранных агентов в выбранных клиентах</p>
                  </div>
                </div>
              </div>

              {error && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="text-sm text-red-700">{error}</div>
                </div>
              )}

              <div className="flex space-x-4 pt-6">
                <button
                  onClick={handleSaveAssignment}
                  className="flex-1 group relative overflow-hidden rounded-2xl py-4 px-6 text-lg font-semibold text-white transition-all duration-300 transform bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 hover:scale-[1.02] hover:shadow-lg"
                >
                  <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                  <div className="relative flex items-center justify-center">
                    💾 Сохранить привязку
                  </div>
                </button>

                <button
                  onClick={() => {
                    setShowAssignmentModal(null);
                    setSelectedAgentIds([]);
                    setSelectedClientIds([]);
                    setError(null);
                  }}
                  className="flex-1 group relative overflow-hidden rounded-2xl py-4 px-6 text-lg font-semibold transition-all duration-300 transform bg-gradient-to-r from-gray-50 to-gray-100 border-2 border-gray-200 text-gray-700 hover:from-gray-100 hover:to-gray-200 hover:border-gray-300 hover:text-gray-900 hover:scale-[1.02] hover:shadow-lg"
                >
                  <div className="absolute inset-0 bg-gray-500 opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
                  <div className="relative flex items-center justify-center">
                    ❌ Отмена
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;